import React from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircle, Github, Unlink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { useGithub, type GithubAuthMode } from '../../../core/hooks/useGithub';
import { LoadingState } from '../../../components/shared/LoadingState';

interface GithubFormValues {
  authMode: GithubAuthMode;
  appId: string;
  clientId: string;
  clientSecret: string;
  privateKey: string;
  webhookSecret: string;
  pat: string;
  patName: string;
}

export const GithubSettings: React.FC = () => {
  const { config, loading, saveConfig, disconnect } = useGithub();
  const [saving, setSaving] = React.useState(false);
  const [authMode, setAuthMode] = React.useState<GithubAuthMode>((config?.authMode || 'github_app') as GithubAuthMode);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<GithubFormValues>({
    defaultValues: {
      authMode: config?.authMode || 'github_app',
      appId: config?.appId || '',
      clientId: config?.clientId || '',
      clientSecret: '',
      privateKey: '',
      webhookSecret: '',
      pat: '',
      patName: '',
    },
  });

  React.useEffect(() => {
    if (config) {
      const nextMode = (config.authMode || 'github_app') as GithubAuthMode;
      setAuthMode(nextMode);
      reset({
        authMode: nextMode,
        appId: config.appId || '',
        clientId: config.clientId || '',
        clientSecret: '',
        privateKey: '',
        webhookSecret: '',
        pat: '',
        patName: '',
      });
    }
  }, [config, reset]);

  const onSave = async (data: GithubFormValues) => {
    try {
      setSaving(true);
      await saveConfig({
        authMode: data.authMode,
        appId: data.appId || undefined,
        clientId: data.clientId || undefined,
        clientSecret: data.clientSecret || undefined,
        privateKey: data.privateKey || undefined,
        webhookSecret: data.webhookSecret || undefined,
        pat: data.pat || undefined,
        patName: data.patName || undefined,
      });
      alert('GitHub integration configuration saved.');
    } catch (error) {
      console.error('Error saving GitHub config:', error);
      alert('Failed to save GitHub configuration.');
    } finally {
      setSaving(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm('Disconnect GitHub integration?')) {
      return;
    }

    try {
      await disconnect();
      alert('GitHub integration disconnected.');
    } catch (error) {
      console.error('Error disconnecting GitHub:', error);
      alert('Failed to disconnect GitHub integration.');
    }
  };

  if (loading) {
    return <LoadingState message="Loading GitHub settings..." />;
  }

  return (
    <div className="space-y-6">
      {config?.connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <CardTitle>GitHub Connected</CardTitle>
              </div>
              <Button variant="destructive" onClick={onDisconnect}>
                <Unlink className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Auth Mode: <span className="font-medium text-foreground">{config.authMode}</span>
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Github className="h-5 w-5" />
            <CardTitle>GitHub Integration</CardTitle>
          </div>
          <CardDescription>
            Configure GitHub App (recommended) or PAT fallback for private repository sync in admin.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Authentication Mode</label>
              <Select
                value={authMode}
                onValueChange={(value) => {
                  const nextMode = value as GithubAuthMode;
                  setAuthMode(nextMode);
                  setValue('authMode', nextMode);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="github_app">GitHub App (Recommended)</SelectItem>
                  <SelectItem value="pat">Personal Access Token</SelectItem>
                </SelectContent>
              </Select>
              <input type="hidden" {...register('authMode', { required: 'Authentication mode is required' })} />
            </div>

            {authMode === 'github_app' ? (
              <div className="space-y-4">
                <Input
                  label="GitHub App ID"
                  {...register('appId', { required: 'GitHub App ID is required for app mode' })}
                  error={errors.appId?.message}
                />
                <Input
                  label="GitHub App Client ID"
                  {...register('clientId')}
                  error={errors.clientId?.message}
                />
                <Input
                  label="GitHub App Client Secret"
                  type="password"
                  {...register('clientSecret')}
                  error={errors.clientSecret?.message}
                />
                <Input
                  label="GitHub App Private Key (PEM)"
                  type="password"
                  {...register('privateKey', { required: 'Private key is required for app mode' })}
                  error={errors.privateKey?.message}
                />
                <Input
                  label="Webhook Secret"
                  type="password"
                  {...register('webhookSecret')}
                  error={errors.webhookSecret?.message}
                />
              </div>
            ) : (
              <div className="space-y-4">
                {config?.patProfiles && config.patProfiles.length > 0 && (
                  <div className="rounded-md border p-3 text-sm">
                    <p className="font-medium mb-1">Saved PAT Profiles</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      {config.patProfiles.map((profile) => (
                        <li key={profile.id}>
                          {profile.name}
                          {config.activePatProfileId === profile.id ? ' (active)' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Input
                  label="PAT Profile Name"
                  placeholder="e.g. ACME Org PAT"
                  {...register('patName', { required: 'PAT profile name is required for PAT mode' })}
                  error={errors.patName?.message}
                />
                <Input
                  label="Personal Access Token"
                  type="password"
                  {...register('pat', { required: 'PAT is required for PAT mode' })}
                  error={errors.pat?.message}
                />
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" loading={saving}>
                Save GitHub Configuration
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
